import type { BlogPostMeta } from '@/app/blog/posts'

/**
 * The two posts offered in an article's inline Read next block.
 *
 * The bottom Related Research grid walks a fixed ring of same-topic posts so
 * that every post receives related links; it is a link-distribution device,
 * not a relevance ranking, and on a broad topic like "food" it pairs a K-Cup
 * guide with an oat milk ranking. The inline block sits where readers still
 * are, so it is chosen for relevance instead: same-topic posts ranked by
 * shared words in the slug (full weight) and title (half weight), each word
 * weighted by how rare it is across the blog, so "k-cup" counts and "best"
 * barely does. Posts below `minScore` are not offered; the caller tops up
 * from the ring.
 */

const FILLER = new Set([
  'a', 'an', 'the', 'of', 'and', 'or', 'for', 'in', 'on', 'to', 'with', 'vs', 'v', 'is', 'are', 'do',
  'does', 'can', 'you', 'your', 'my', 'it', 'what', 'how', 'why', 'have', 'has', 'x', 'microplastics',
  'microplastic', '2025', '2026', 'best', 'ranked', 'brand', 'brands', 'safe', 'really', 'actually',
  'truth', 'verdict', 'evidence', 'honest', 'answer', 'guide', 'audit', 'audited', 'compared',
  'comparison', 'explained', 'release', 'shed', 'leach', 'contain', 'free', 'plastic', 'without',
  'which', 'most', 'least', 'worst',
])

function stem(word: string): string {
  if (word.length > 4 && word.endsWith('es') && !word.endsWith('sses')) return word.slice(0, -2)
  if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1)
  return word
}

export function words(text: string): string[] {
  const out = new Set<string>()
  for (const w of text.toLowerCase().split(/[^a-z0-9]+/)) {
    if (w && !FILLER.has(w)) out.add(stem(w))
  }
  return Array.from(out)
}

interface Doc {
  post: BlogPostMeta
  slug: string[]
  title: string[]
}

interface Index {
  docs: Doc[]
  idf: (word: string) => number
}

const cache = new WeakMap<readonly BlogPostMeta[], Index>()

function indexFor(posts: readonly BlogPostMeta[]): Index {
  const hit = cache.get(posts)
  if (hit) return hit
  const docs = posts.map((post) => ({ post, slug: words(post.slug), title: words(post.title) }))
  const df = new Map<string, number>()
  for (const d of docs) {
    new Set(d.slug.concat(d.title)).forEach((w) => df.set(w, (df.get(w) ?? 0) + 1))
  }
  const n = docs.length
  const index = { docs, idf: (w: string) => Math.log(n / (df.get(w) ?? 1)) }
  cache.set(posts, index)
  return index
}

function cosine(a: string[], b: string[], idf: (w: string) => number): number {
  const inB = new Set(b)
  let shared = 0
  let normA = 0
  let normB = 0
  for (const w of a) {
    const x = idf(w) ** 2
    normA += x
    if (inB.has(w)) shared += x
  }
  for (const w of b) normB += idf(w) ** 2
  return normA && normB ? shared / Math.sqrt(normA * normB) : 0
}

export function readNextPosts(
  slug: string,
  posts: readonly BlogPostMeta[],
  limit = 2,
  minScore = 0.15
): BlogPostMeta[] {
  const { docs, idf } = indexFor(posts)
  const current = docs.find((d) => d.post.slug === slug)
  if (!current) return []
  return docs
    .filter((d) => d !== current && d.post.topic === current.post.topic)
    .map((d) => ({
      post: d.post,
      score: cosine(current.slug, d.slug, idf) + 0.5 * cosine(current.title, d.title, idf),
    }))
    .filter((s) => s.score >= minScore)
    .sort((x, y) => y.score - x.score || x.post.slug.localeCompare(y.post.slug))
    .slice(0, limit)
    .map((s) => s.post)
}
